import {
  ChevronDownIcon,
  LogOutIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Avatar, Button } from '@splitfiesta/ui';

import { useRouter } from '@/hooks/useRouteChangeRouter';

import { queryClient } from '@/lib/queryClient';

import { useAuthStore } from '@/stores/authStore';

interface UserMenuProps {
  mobile?: boolean;
}

export function UserMenu({ mobile = false }: UserMenuProps) {
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const { t } = useTranslation();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load auth namespace for logout text
  useNamespace('auth');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    // Clear all React Query cache to prevent data leakage between users
    queryClient.clear();
    logout();
    router.push('/login');
  };

  const menuItems = [
    {
      name: t('common:navigation.profile'),
      href: '/profile',
      icon: UserIcon,
      onClick: () => {
        router.push('/profile');
        setIsOpen(false);
      },
    },
    {
      name: t('common:navigation.settings'),
      href: '/settings',
      icon: SettingsIcon,
      onClick: () => {
        router.push('/settings');
        setIsOpen(false);
      },
    },
    {
      name: t('auth:actions.logout'),
      href: '#',
      icon: LogOutIcon,
      onClick: handleLogout,
    },
  ];

  if (!user) {
    return null;
  }

  if (mobile) {
    return (
      <div className="space-y-3" data-testid="user-menu-mobile">
        <div className="flex items-center">
          <div className="shrink-0">
            <Avatar
              name={user.name}
              src={(user as { profileImage?: string }).profileImage}
              size="md"
              data-testid="user-menu-mobile-avatar"
            />
          </div>
          <div className="ml-3">
            <div className="text-base font-medium text-gray-800 dark:text-gray-200">
              {user.name}
            </div>
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {user.email}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          {menuItems.map(item => {
            const Icon = item.icon;
            return (
              <Button
                key={item.name}
                variant="ghost"
                size="md"
                onClick={item.onClick}
                leftIcon={<Icon className="w-5 h-5" />}
                className="w-full justify-start px-4 py-2 text-base font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid={
                  item.href === '/profile'
                    ? 'user-menu-mobile-profile-link'
                    : item.href === '/settings'
                      ? 'user-menu-mobile-settings-link'
                      : 'user-menu-mobile-logout-button'
                }
              >
                {item.name}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef} data-testid="user-menu">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="user-menu-button"
        data-state={isOpen ? 'open' : 'closed'}
        className="flex items-center rounded-full px-2 py-1 menu-btn text-gray-700 dark:text-gray-300"
      >
        <Avatar
          name={user.name}
          src={user.profileImage ?? undefined}
          size="sm"
          data-testid="user-menu-avatar"
        />
        <span className="ml-2 text-sm font-medium hidden md:block">
          {user.name}
        </span>
        <ChevronDownIcon className="ml-1 w-4 h-4 text-gray-400 dark:text-gray-500 hidden md:block" />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 min-w-max bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-gray-700 overflow-hidden z-50"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {user.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {user.email}
            </p>
          </div>

          <div className="py-1">
            {menuItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={item.onClick}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors text-left"
                  data-testid={
                    item.href === '/profile'
                      ? 'user-menu-profile-link'
                      : item.href === '/settings'
                        ? 'user-menu-settings-link'
                        : 'user-menu-logout-button'
                  }
                >
                  <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
